import { useEffect, useState } from "react";
import { toast } from "sonner";
import { rpc } from "../../../lib/rpc";
import { enqueueRpc } from "../../../lib/enqueueRpc";
import { useFlowOperationId } from "../../../hooks/useFlowOperationId";
import { useScanPrefill } from "../../../hooks/useScanPrefill";
import { FlowShell } from "../../../components/mobile/FlowShell";
import { ScanBox } from "../../../components/mobile/ScanBox";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

export default function AusleiheFlow() {
    const flow = useFlowOperationId();
    const prefill = useScanPrefill();
    const [assets, setAssets] = useState([]);
    const [assetId, setAssetId] = useState("");
    const [person, setPerson] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [scanRestartKey, setScanRestartKey] = useState(0);
    const [scanError, setScanError] = useState("");

    useEffect(() => {
        if (prefill?.assetId) {
            setAssetId(prefill.assetId);
            toast.success("Ausleihgegenstand aus Scan übernommen.");
        }
    }, [prefill]);

    useEffect(() => {
        rpc.get("/master/assets?status=available").then((res) => {
            if (res.ok) setAssets(res.data.filter((asset) => asset.is_loanable));
        });
    }, []);

    const submit = async () => {
        if (!assetId) return toast.error("Ausleihgegenstand wählen oder scannen.");
        if (!person.trim()) return toast.error("Name oder Kontakt Pflicht.");
        setSubmitting(true);
        const res = await enqueueRpc({
            path: "/movements",
            rpcName: "Ausleihe",
            summary: `${person}`,
            payload: {
                client_operation_id: flow.clientOperationId,
                type: "loan",
                asset_id: assetId,
                person,
            },
        });
        setSubmitting(false);
        if (!res.ok) {
            if (res.retryable) toast.warning("Offline -- Ausleihe wartet in der Queue.");
            else return toast.error(res.error);
        } else {
            toast.success("Ausleihe gebucht.");
        }
        flow.rotate();
        setAssetId("");
        setScanRestartKey((key) => key + 1);
    };

    return (
        <FlowShell
            testId="flow-ausleihe"
            title="Ausleihe"
            kind="Schnellflow"
            onSubmit={submit}
            submitDisabled={!assetId || !person.trim()}
            submitting={submitting}
        >
            <ScanBox
                placeholder="Ausleihgegenstand oder Kennung scannen"
                restartKey={scanRestartKey}
                onResolved={(raw, resolved) => {
                    if (resolved?.kind === "asset") {
                        setAssetId(resolved.id);
                        setScanError("");
                        toast.success(`Ausleihgegenstand erkannt: ${resolved.label}`);
                    } else {
                        setAssetId("");
                        setScanError(`Code „${raw}“ ist keinem Ausleihgegenstand zugeordnet. Bitte erneut scannen oder einen Eintrag aus der Liste wählen.`);
                    }
                }}
            />
            {scanError ? (
                <div role="alert" className="rounded-md border border-destructive/60 bg-destructive/10 p-3 text-sm text-foreground" data-testid="ausleihe-scan-error">
                    {scanError}
                </div>
            ) : null}
            {assets.length > 0 ? (
                <div className="flex flex-col gap-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Ausleihgegenstand wählen</Label>
                    <Select value={assetId} onValueChange={(value) => { setAssetId(value); setScanError(""); }}>
                        <SelectTrigger className="h-12"><SelectValue placeholder="Ausleihgegenstand wählen" /></SelectTrigger>
                        <SelectContent>
                            {assets.map((asset) => (
                                <SelectItem key={asset.id} value={asset.id}>
                                    {asset.asset_tag || asset.serial_number || asset.id.slice(0, 8)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            ) : null}
            <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Interne Kennung</Label>
                <Input
                    value={assetId}
                    onChange={(e) => { setAssetId(e.target.value); setScanError(""); }}
                    placeholder="Interne Kennung oder Seriennummer"
                    className="h-12"
                    aria-label="Interne Kennung"
                    data-testid="ausleihe-asset-id"
                />
            </div>
            <div className="flex flex-col gap-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Ausgeliehen an
                </Label>
                <Input
                    value={person}
                    onChange={(e) => setPerson(e.target.value)}
                    placeholder="Name, Team oder Kontakt"
                    className="h-12"
                    aria-label="Ausgeliehen an"
                    data-testid="ausleihe-person"
                />
            </div>
        </FlowShell>
    );
}
